import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Check, X as XIcon, Trash2 } from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
];

export const ReportsQueue = () => {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0, dismissed: 0 });
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(true);

  const refreshCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/counts");
      setCounts(data);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async (state) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports?state=${state}`);
      setReports(data || []);
    } catch {
      toast.error("Could not load reports.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); refreshCounts(); }, [tab, load, refreshCounts]);

  const act = async (id, action) => {
    try {
      await api.post(`/reports/${id}/${action}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      const msg = {
        resolve: "Marked resolved.",
        dismiss: "Dismissed.",
        "delete-target": "Content removed and report resolved.",
      }[action] || "Updated.";
      toast.success(msg);
      refreshCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update.");
    }
  };

  const deleteAndResolve = async (r) => {
    const ttype = r.target_type;
    if (!(ttype === "post" || ttype === "comment")) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete this ${ttype} permanently and resolve the report?`)) return;
    act(r.id, "delete-target");
  };

  return (
    <section
      data-testid="reports-queue"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        padding: 28,
        marginBottom: 40,
      }}
    >
      <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Flag size={12} strokeWidth={1.6} /> Moderation queue
      </div>
      <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 4, marginBottom: 8, fontWeight: 500 }}>
        Reports
        {counts.open > 0 && (
          <span style={{ marginLeft: 12, color: "var(--brand-accent)", fontSize: 18 }}>
            {counts.open}
          </span>
        )}
      </h2>
      <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic", marginBottom: 18 }}>
        Every report a member files lands here. Read, decide, and act — or clear it if all is well.
      </p>

      {/* Filter tabs */}
      <div
        role="tablist"
        style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border-default)", marginBottom: 22 }}
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`reports-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: 12,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                fontWeight: active ? 600 : 400,
                color: active ? "var(--brand-accent)" : "var(--text-secondary)",
                borderBottom: active ? "2px solid var(--brand-accent)" : "2px solid transparent",
                marginBottom: -1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 11,
                  color: active ? "var(--brand-accent)" : "var(--text-muted)",
                  background: active ? "rgba(114, 47, 55, 0.08)" : "var(--bg-alt)",
                  padding: "1px 8px",
                  borderRadius: 10,
                  letterSpacing: 0,
                }}
              >
                {counts[t.key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>Loading…</p>
      ) : reports.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 17 }}>
          {tab === "open" ? "No open reports. All is calm in the salon." : `Nothing in ${tab}.`}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {reports.map((r) => (
            <div
              key={r.id}
              data-testid={`report-${r.id}`}
              style={{
                border: "1px solid var(--border-default)",
                padding: 18,
                background: "var(--bg-alt)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                <div>
                  <div className="overline" style={{ color: "var(--brand-accent)" }}>
                    {r.target_type} · {fmtWhen(r.created_at)}
                  </div>
                  <div style={{ marginTop: 4, fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}>
                    Reported by{" "}
                    <a
                      href={`/profile/${r.reporter?.username || ""}`}
                      style={{ color: "var(--brand-accent)", textDecoration: "none", borderBottom: "1px solid var(--brand-accent)" }}
                    >
                      {r.reporter?.display_name || "unknown"}
                    </a>{" "}
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>· {r.target_type} id: {r.target_id.slice(0, 8)}…</span>
                  </div>
                </div>
                {r.state && r.state !== "open" && (
                  <span
                    className="overline"
                    style={{
                      color: r.state === "resolved" ? "var(--brand-accent)" : "var(--text-muted)",
                      background: "var(--surface)",
                      padding: "4px 10px",
                      border: "1px solid var(--border-default)",
                    }}
                  >
                    {r.state}
                  </span>
                )}
              </div>
              <blockquote
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: "italic",
                  fontSize: 17,
                  lineHeight: 1.5,
                  color: "var(--text-primary)",
                  padding: "10px 16px",
                  borderLeft: "3px solid var(--brand-accent)",
                  background: "var(--surface)",
                  margin: "6px 0 14px 0",
                }}
              >
                &ldquo;{r.reason}&rdquo;
              </blockquote>
              {tab === "open" && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {(r.target_type === "post" || r.target_type === "comment") && (
                    <button
                      type="button"
                      onClick={() => deleteAndResolve(r)}
                      data-testid={`delete-target-${r.id}`}
                      className="btn-primary"
                      style={{ background: "var(--brand-accent)", display: "inline-flex", alignItems: "center", gap: 8 }}
                    >
                      <Trash2 size={14} /> Delete {r.target_type} + resolve
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => act(r.id, "resolve")}
                    data-testid={`resolve-${r.id}`}
                    className="btn-secondary"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                  >
                    <Check size={14} /> Resolve
                  </button>
                  <button
                    type="button"
                    onClick={() => act(r.id, "dismiss")}
                    data-testid={`dismiss-${r.id}`}
                    className="btn-secondary"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                  >
                    <XIcon size={14} /> Dismiss
                  </button>
                  {(r.target_type === "user" || r.target_type === "post" || r.target_type === "comment") && (
                    <a
                      href={r.target_type === "post" ? `/post/${r.target_id}` : (r.target_type === "user" ? `/profile/${r.target_id}` : "#")}
                      className="btn-secondary"
                      style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
                    >
                      View {r.target_type}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ReportsQueue;
